import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { banners } from "@/mocks/banners";
import { getMediaById } from "@/lib/media";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const timerRef = useRef<number | null>(null);
  const touchStartX = useRef(0);

  const goTo = useCallback((index: number) => {
    setActive((index + banners.length) % banners.length);
  }, []);

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setActive((v) => (v + 1) % banners.length);
    }, 5500);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [active]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) < 50) return;
    if (delta < 0) next();
    else prev();
  };

  const current = banners[active];
  const media = getMediaById(current.mediaId);

  return (
    <section
      className="relative w-full overflow-hidden bg-secondary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative h-[440px] w-full md:h-[600px]">
        {banners.map((b, i) => (
          <div
            key={b.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={b.image}
              alt={`${b.title} - 红狐影视焦点推荐`}
              title={`${b.title} + 红狐影视`}
              className="h-full w-full object-cover object-center"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/90 via-secondary-950/65 to-secondary-950/25"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/70 via-transparent to-transparent"></div>
      </div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3.5 py-1.5 text-xs font-semibold text-background-50">
              <i className="ri-fire-fill"></i>
              {current.tag}
            </span>
            <h2 className="font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
              {current.title}
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-secondary-100 md:text-base">
              {current.subtitle}
            </p>
            {media && (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-secondary-200 md:text-sm">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400"></i>
                  {media.rating.toFixed(1)}
                </span>
                <span>{media.year}</span>
                <span>{media.region}</span>
                <span>{media.genres.join(" · ")}</span>
              </div>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.mediaId}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看详情
              </Link>
              <Link
                to={media ? `/category/${media.category}` : "/category/movie"}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20 cursor-pointer"
              >
                <i className="ri-compass-3-line text-lg"></i>
                浏览同类
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={prev}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-500 md:flex md:left-6 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-500 md:flex md:right-6 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {banners.map((b, i) => (
          <button
            key={b.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === active ? "w-8 bg-primary-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}